import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { CloudinaryImage, getCloudinaryUrl } from "@/components/CloudinaryMedia";
import { usePublicMedia, type PublicMedia, parseTags } from "@/hooks/useMedia";

// Static fallback images (used when CMS has no portfolio items)
import bathroom1 from "@assets/generated_images/Luxury_bathroom_portfolio_piece_81fb4bb7.png";
import kitchen1 from "@assets/generated_images/Gourmet_kitchen_renovation_example_d0e6127e.png";
import deck from "@assets/generated_images/Custom_deck_construction_project_68cfd878.png";
import office from "@assets/generated_images/Home_office_renovation_showcase_4ff60dbc.png";
import handyman from "@assets/generated_images/Handyman_services_in_action_6eae28f7.png";

interface PortfolioItem {
  id: string | number;
  image: string;
  publicId?: string; // If from CMS
  category: string;
  title: string;
  description: string;
  details: string[];
  isCloudinary?: boolean;
}

// Static fallback portfolio items
const staticPortfolioItems: PortfolioItem[] = [
  {
    id: "static-1",
    image: bathroom1,
    category: "Bathrooms",
    title: "Spa-Inspired Master Bath",
    description: "Complete bathroom renovation featuring luxury fixtures, custom tile work, and modern lighting. Transformed a dated space into a serene spa-like retreat.",
    details: ["Custom tile installation", "Frameless glass shower", "Double vanity with quartz countertops", "Heated flooring"],
  },
  {
    id: "static-2",
    image: kitchen1,
    category: "Kitchens",
    title: "Gourmet Kitchen Design",
    description: "Full kitchen remodel with high-end appliances, custom cabinetry, and granite countertops. Created a functional and beautiful space for cooking and entertaining.",
    details: ["Custom cabinetry", "Granite countertops", "Stainless steel appliances", "Large center island"],
  },
  {
    id: "static-3",
    image: deck,
    category: "Woodwork",
    title: "Custom Deck Construction",
    description: "Outdoor living space expansion with composite decking, built-in seating, and professional railing system. Perfect for entertaining and relaxation.",
    details: ["Composite decking material", "Built-in bench seating", "Cable railing system", "LED lighting"],
  },
  {
    id: "static-4",
    image: office,
    category: "Renovations",
    title: "Home Office Transformation",
    description: "Converted spare bedroom into a functional home office with custom built-ins, modern lighting, and professional finishes.",
    details: ["Custom built-in shelving", "Recessed lighting", "Hardwood flooring", "Dedicated electrical circuits"],
  },
  {
    id: "static-5",
    image: handyman,
    category: "Renovations",
    title: "Complete Home Repair & Upgrade",
    description: "Comprehensive home improvement project including interior painting, fixture updates, and minor repairs throughout the property.",
    details: ["Interior painting", "Fixture replacement", "Minor repairs", "Hardware upgrades"],
  },
];

/**
 * Convert CMS media to portfolio item format
 */
function cmsToPortfolioItem(media: PublicMedia): PortfolioItem {
  const tags = parseTags(media.tags);
  // Use first tag as category, or default based on category field
  const category = tags[0] || media.category || "Projects";
  
  return {
    id: media.id,
    image: getCloudinaryUrl(media.publicId, { width: 800, height: 600 }),
    publicId: media.publicId,
    category: category.charAt(0).toUpperCase() + category.slice(1),
    title: media.title || "Project",
    description: media.description || "A beautiful renovation project by HHS Build.",
    details: tags.slice(1), // Use remaining tags as details
    isCloudinary: true,
  };
}

export default function Portfolio() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Fetch portfolio media from CMS
  const { data: cmsMedia, isLoading } = usePublicMedia("portfolio");

  // Combine CMS media with static fallbacks
  const portfolioItems = useMemo(() => {
    if (cmsMedia && cmsMedia.length > 0) {
      // Use CMS media if available
      return cmsMedia.map(cmsToPortfolioItem);
    }
    // Fall back to static items
    return staticPortfolioItems;
  }, [cmsMedia]);

  // Extract unique categories from items
  const categories = useMemo(() => {
    const cats = new Set(portfolioItems.map((item) => item.category));
    return ["All", ...Array.from(cats)];
  }, [portfolioItems]);

  const filteredItems =
    selectedCategory === "All"
      ? portfolioItems
      : portfolioItems.filter((item) => item.category === selectedCategory);

  const handleItemClick = (item: PortfolioItem) => {
    setSelectedItem(item);
    setIsDialogOpen(true);
  };

  const handleNext = () => {
    if (!selectedItem) return;
    const currentIndex = portfolioItems.findIndex((item) => item.id === selectedItem.id);
    const nextIndex = (currentIndex + 1) % portfolioItems.length;
    setSelectedItem(portfolioItems[nextIndex]);
  };

  const handlePrev = () => {
    if (!selectedItem) return;
    const currentIndex = portfolioItems.findIndex((item) => item.id === selectedItem.id);
    const prevIndex = (currentIndex - 1 + portfolioItems.length) % portfolioItems.length;
    setSelectedItem(portfolioItems[prevIndex]);
  };

  return (
    <section id="portfolio" className="py-16 md:py-24 lg:py-32 relative" data-testid="section-portfolio">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 tracking-tight gradient-text"
            data-testid="text-portfolio-title"
          >
            Our Work
          </h2>
          <p
            className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8"
            data-testid="text-portfolio-subtitle"
          >
            Explore our portfolio of completed projects
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="default"
                onClick={() => {
                  console.log(`Filter changed to: ${category}`);
                  setSelectedCategory(category);
                }}
                className={`font-medium ${selectedCategory === category ? 'glow-cyan' : ''}`}
                data-testid={`button-filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredItems.map((item) => (
              <Card
                key={item.id}
                className="group overflow-hidden glass-card border-cyan-500/20 hover-lift hover-glow-cyan transition-all cursor-pointer"
                onClick={() => handleItemClick(item)}
                data-testid={`card-portfolio-${item.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {item.isCloudinary && item.publicId ? (
                    <CloudinaryImage
                      publicId={item.publicId}
                      alt={item.title}
                      width={600}
                      height={450}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/80 via-purple-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none">
                    <Badge className="mb-2 bg-cyan-500/90 text-black border-0" data-testid={`badge-category-${item.id}`}>
                      {item.category}
                    </Badge>
                    <h3
                      className="text-xl font-heading font-bold text-white drop-shadow-lg"
                      data-testid={`text-portfolio-title-${item.id}`}
                    >
                      {item.title}
                    </h3>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl p-0 glass-card border-cyan-500/30" data-testid="dialog-portfolio">
            {selectedItem && (
              <div className="relative">
                <div className="aspect-[16/10] overflow-hidden rounded-t-lg">
                  {selectedItem.isCloudinary && selectedItem.publicId ? (
                    <CloudinaryImage
                      publicId={selectedItem.publicId}
                      alt={selectedItem.title}
                      width={1200}
                      height={750}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={selectedItem.image}
                      alt={selectedItem.title}
                      className="w-full h-full object-cover"
                      data-testid="img-portfolio-lightbox"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>

                <div className="absolute top-4 left-4">
                  <Badge className="text-sm bg-cyan-500/90 text-black border-0" data-testid="badge-lightbox-category">
                    {selectedItem.category}
                  </Badge>
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-1/2 left-4 transform -translate-y-1/2 glass-surface border-cyan-500/30 text-cyan-400 hover:text-cyan-300"
                  onClick={handlePrev}
                  aria-label="View previous project"
                  data-testid="button-portfolio-prev"
                >
                  <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                </Button>

                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-1/2 right-4 transform -translate-y-1/2 glass-surface border-cyan-500/30 text-cyan-400 hover:text-cyan-300"
                  onClick={handleNext}
                  aria-label="View next project"
                  data-testid="button-portfolio-next"
                >
                  <ChevronRight className="h-6 w-6" aria-hidden="true" />
                </Button>

                <div className="p-6">
                  <DialogHeader>
                    <DialogTitle className="text-2xl md:text-3xl font-heading font-bold mb-3 gradient-text" data-testid="text-lightbox-title">
                      {selectedItem.title}
                    </DialogTitle>
                    <DialogDescription className="text-base text-gray-300 mb-4" data-testid="text-lightbox-description">
                      {selectedItem.description}
                    </DialogDescription>
                  </DialogHeader>

                  {selectedItem.details.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm uppercase tracking-wide text-cyan-400" data-testid="text-lightbox-features-title">
                        Key Features:
                      </h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedItem.details.map((detail, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-gray-200" data-testid={`text-lightbox-detail-${index}`}>
                            <div className="h-1.5 w-1.5 rounded-full bg-cyan-500 glow-cyan" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
